import { useState, useEffect } from 'react';
import { 
  Trophy, 
  Plus,
  Users,
  Clock,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Edit,
  Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { tournamentsAPI } from '../lib/api';
import { formatCurrency, formatDateTime, getStatusColor, getGameVariantLabel } from '../lib/utils';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  const color = getStatusColor(status);
  const colorClasses = {
    success: 'badge-success',
    warning: 'badge-warning',
    danger: 'badge-danger',
    info: 'badge-info',
    neutral: 'badge-neutral',
  };
  
  return (
    <span className={`badge ${colorClasses[color]}`}>
      {status?.replace('_', ' ')}
    </span>
  );
};

export default function Tournaments() {
  const [tournaments, setTournaments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [createDialog, setCreateDialog] = useState(false);
  const [newTournament, setNewTournament] = useState({
    name: '',
    variant: 'points_rummy',
    entry_fee: 100,
    prize_pool: 10000,
    guaranteed_prize: true,
    max_participants: 100,
    start_time: '',
    late_registration: true,
  });

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const params = {
        skip: page * limit,
        limit,
        status: statusFilter || undefined,
      };
      const response = await tournamentsAPI.getAll(params);
      setTournaments(response.data.tournaments);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch tournaments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTournaments();
  }, [page, statusFilter]);

  const handleCreate = async () => {
    try {
      const data = {
        ...newTournament,
        start_time: new Date(newTournament.start_time).toISOString(),
      };
      await tournamentsAPI.create(data);
      toast.success('Tournament created');
      setCreateDialog(false);
      setNewTournament({
        name: '',
        variant: 'points_rummy',
        entry_fee: 100,
        prize_pool: 10000,
        guaranteed_prize: true,
        max_participants: 100,
        start_time: '',
        late_registration: true,
      });
      fetchTournaments();
    } catch (error) {
      toast.error('Failed to create tournament');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await tournamentsAPI.update(id, { status: newStatus });
      toast.success('Tournament status updated');
      fetchTournaments();
    } catch (error) {
      toast.error('Failed to update tournament');
    }
  };

  return (
    <div data-testid="tournaments-page">
      {/* Header */}
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Tournament Management</h1>
          <p className="page-subtitle">Create and manage tournaments</p>
        </div>
        <Button onClick={() => setCreateDialog(true)} data-testid="create-tournament-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Tournament
        </Button>
      </div>

      {/* Stats */}
      <div className="widget-grid mb-6">
        <div className="stat-card">
          <Trophy className="w-5 h-5 text-primary mb-2" />
          <p className="stat-value">{tournaments.filter(t => t.status === 'upcoming' || t.status === 'registering').length}</p>
          <p className="stat-label">Upcoming</p>
        </div>
        <div className="stat-card">
          <Clock className="w-5 h-5 text-amber-400 mb-2" />
          <p className="stat-value">{tournaments.filter(t => t.status === 'in_progress').length}</p>
          <p className="stat-label">In Progress</p>
        </div>
        <div className="stat-card">
          <Users className="w-5 h-5 text-blue-400 mb-2" />
          <p className="stat-value">{tournaments.reduce((sum, t) => sum + t.current_participants, 0)}</p>
          <p className="stat-label">Total Participants</p>
        </div>
        <div className="stat-card">
          <DollarSign className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="stat-value">{formatCurrency(tournaments.reduce((sum, t) => sum + t.prize_pool, 0))}</p>
          <p className="stat-label">Total Prize Pool</p>
        </div>
      </div>

      {/* Table */}
      <div className="data-table-container">
        <div className="data-table-header">
          <h3 className="data-table-title">All Tournaments</h3>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="tournament-status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="registering">Registering</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Table>
          <TableHeader>
            <TableRow className="table-header">
              <TableHead>Tournament</TableHead>
              <TableHead>Variant</TableHead>
              <TableHead className="text-right">Entry Fee</TableHead>
              <TableHead className="text-right">Prize Pool</TableHead>
              <TableHead>Participants</TableHead>
              <TableHead>Start Time</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                </TableCell>
              </TableRow>
            ) : tournaments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                  No tournaments found
                </TableCell>
              </TableRow>
            ) : (
              tournaments.map((tournament) => (
                <TableRow key={tournament.id} className="table-row" data-testid={`tournament-row-${tournament.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Trophy className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{tournament.name}</p>
                        {tournament.guaranteed_prize && (
                          <span className="text-xs text-amber-400">GPP</span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getGameVariantLabel(tournament.variant)}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(tournament.entry_fee)}</TableCell>
                  <TableCell className="text-right font-mono text-emerald-400">{formatCurrency(tournament.prize_pool)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-muted-foreground" />
                      <span>{tournament.current_participants}/{tournament.max_participants}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{formatDateTime(tournament.start_time)}</TableCell>
                  <TableCell>
                    <StatusBadge status={tournament.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Select 
                      value={tournament.status}
                      onValueChange={(v) => handleStatusChange(tournament.id, v)}
                    >
                      <SelectTrigger className="w-32 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="upcoming">Upcoming</SelectItem>
                        <SelectItem value="registering">Registering</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <p className="text-sm text-muted-foreground">
            Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Create Tournament Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Tournament</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tournament Name</Label>
              <Input
                placeholder="Enter tournament name"
                value={newTournament.name}
                onChange={(e) => setNewTournament({ ...newTournament, name: e.target.value })}
                data-testid="tournament-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Variant</Label>
              <Select
                value={newTournament.variant}
                onValueChange={(v) => setNewTournament({ ...newTournament, variant: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="points_rummy">Points Rummy</SelectItem>
                  <SelectItem value="pool_101">Pool 101</SelectItem>
                  <SelectItem value="pool_201">Pool 201</SelectItem>
                  <SelectItem value="deals_rummy">Deals Rummy</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Entry Fee</Label>
                <Input
                  type="number"
                  value={newTournament.entry_fee}
                  onChange={(e) => setNewTournament({ ...newTournament, entry_fee: parseFloat(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Prize Pool</Label>
                <Input
                  type="number"
                  value={newTournament.prize_pool}
                  onChange={(e) => setNewTournament({ ...newTournament, prize_pool: parseFloat(e.target.value) })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Max Participants</Label>
              <Input
                type="number"
                value={newTournament.max_participants}
                onChange={(e) => setNewTournament({ ...newTournament, max_participants: parseInt(e.target.value) })}
              />
            </div>
            <div className="space-y-2">
              <Label>Start Time</Label>
              <Input
                type="datetime-local"
                value={newTournament.start_time}
                onChange={(e) => setNewTournament({ ...newTournament, start_time: e.target.value })}
                data-testid="tournament-start-time"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Guaranteed Prize Pool (GPP)</Label>
              <Switch
                checked={newTournament.guaranteed_prize}
                onCheckedChange={(v) => setNewTournament({ ...newTournament, guaranteed_prize: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Late Registration</Label>
              <Switch
                checked={newTournament.late_registration}
                onCheckedChange={(v) => setNewTournament({ ...newTournament, late_registration: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreate}
              disabled={!newTournament.name || !newTournament.start_time}
              data-testid="confirm-create-tournament-btn"
            >
              Create Tournament
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
